<template>
    <article
        class="product-option bundle-template-options"
    >
        <v-list
            v-if="currentBundleTemplate"
            v-model:opened="listExpands"
            class="d-flex flex-column rounded-lg border-thin"
            :lines="false"
        >
            <template
                v-for="([bundleTmplLineUid, line], index) of currentBundleTemplate.lines"
                :key="`bundle-template-${bundleTmplLineUid}-${index}`"
            >
                <v-list-group
                    class="product-option-item"
                    :value="bundleTmplLineUid"
                    :collapse-icon="!line.isRequired ? mdiCheckboxMarked : ''"
                    :expand-icon="!line.isRequired ? mdiCheckboxBlankOutline : ''"
                    fluid
                >
                    <template
                        #activator="{ props }"
                    >
                        <v-list-item
                            v-bind="!line.isRequired ? props : void(0)"
                            :active="false"
                            :link="!line.isRequired"
                        >
                            <label
                                class="d-flex align-center text-uppercase font-weight-bold font-size-7"
                            >
                                <BundleTemplatesLineItemTitle
                                    :model-value="model[bundleTmplLineUid]"
                                    :line="line"
                                    :is-invalid="invalidLineUids.has(bundleTmplLineUid)"
                                ></BundleTemplatesLineItemTitle>

                                <v-spacer></v-spacer>
                                <v-chip
                                    v-if="!line.isRequired"
                                    color="secondary"
                                    density="compact"
                                    size="small"
                                    variant="outlined"
                                    label
                                >
                                    OPTIONAL
                                </v-chip>
                            </label>
                        </v-list-item>
                    </template>
                    <v-list-item
                        class="pb-4"
                    >
                        <DebugContent>
                            <div>
                                <details>
                                    <summary class="cursor-pointer">DEBUG: Line</summary>
                                    <ul class="font-size-7">
                                        <li>
                                            <div>BUNDLE LINE UID:</div>
                                            <strong>{{ bundleTmplLineUid }}</strong>
                                        </li>
                                        <li>
                                            <div>BUNDLE LINE MODEL:</div>
                                            <strong>{{ model[bundleTmplLineUid] }}</strong>
                                        </li>
                                    </ul>

                                </details>
                                <v-divider class="my-4"></v-divider>
                            </div>
                        </DebugContent>

                        <BundleTemplatesLine
                            :model-value="model[bundleTmplLineUid]"
                            :line="line"
                            :line-uid="bundleTmplLineUid"
                            :skus-map="currentBundleTemplate.skusMaps.get(bundleTmplLineUid)!"
                            :attrs-map="currentBundleTemplate.attrsMap"
                            :is-enabled="listExpands.includes(bundleTmplLineUid)"
                            class="d-flex flex-column gap-4"
                            @update:model-value="onChange(bundleTmplLineUid, $event)"
                        ></BundleTemplatesLine>
                    </v-list-item>
                </v-list-group>

                <v-divider
                    v-if="index < (currentBundleTemplate.lines.size - 1)"
                ></v-divider>
            </template>
        </v-list>

        <div
            v-if="isLoggedIn"
            class="d-flex mt-2"
        >
            <v-spacer></v-spacer>
            <v-btn
                class=""
                variant="flat"
                color="accent-darken-2"
                size="small"
                :disabled="!isValid"
                @click="onSaveBundleConfig"
            >
                Save Customization
            </v-btn>
        </div>

        <ResponsiveModal
            v-model="modalSaveBundleConfig"
            width="480px"
        >
            <template #default="{ onExit, openCount }">
                <BundleSaveConfigForm
                    mode="create"
                    :processed-models="processedModels"
                    :sku="sku"
                    @on-save="onBundleSaved($event), onExit()"
                    @on-exit="onExit()"
                ></BundleSaveConfigForm>
            </template>
        </ResponsiveModal>
    </article>
</template>

<script lang="ts" setup>
    import BundleTemplatesLine from '~/components/products/product/bundle-templates/BundleTemplatesLine/BundleTemplatesLine.vue';
    import BundleTemplatesLineItemTitle from '~/components/products/product/bundle-templates/BundleTemplatesLineItemTitle/BundleTemplatesLineItemTitle.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';

    import {
        mdiCheckboxBlankOutline,
        mdiCheckboxMarked
    } from '@mdi/js';
    import type {
        GenericObject,
        Sku,
        BundleTemplateLineSkuData,
        BundleTemplateProcessedModel,
        BundleTemplateProcessedModelItem,
        BundleTemplateProcessed,
        Bundle
    } from '~/types';
    import {
        useBundleUtils,
        useBundleTemplates,
        useDebug
    } from '~/composables';
    import { xor as lo_xor } from 'es-toolkit';

    const ResponsiveModal = defineAsyncComponent(() => import('x1-base/components/ResponsiveModal/ResponsiveModal.vue'));
    const BundleSaveConfigForm = defineAsyncComponent(() => import('~/components/products/product/bundle/BundleSaveConfigForm/BundleSaveConfigForm.vue'));

    const props = withDefaults(
        defineProps<{
            modelValue: Nilish<BundleTemplateProcessedModel>;
            isValid: boolean;
            currentBundleTemplate: Nilish<BundleTemplateProcessed>;
            currentBundleTemplateUid: Nilish<string>;
            sku?: Nilish<Sku>;
            isDirty?: boolean;
        }>(),
        {
            isValid: false
        }
    );

    const emit = defineEmits<{
        'bundle-saved': [value: Bundle];
        'update:modelValue': [value: Nullable<BundleTemplateProcessedModel>];
        'update:is-valid': [value: boolean];
        'update:is-dirty': [value: boolean];
        'on-is-modified-change': [value: boolean];
    }>();

    const nuxtApp = useNuxtApp();
    const {
        isLoggedIn
    } = nuxtApp.$user;
    const debugConsole = useDebug();
    const {
        modelHydrate,
        modelDehydrate,
        validateProcessedModel
    } = useBundleTemplates();

    const {
        getOutputModel,
    } = useBundleUtils();

    type LocalModel = GenericObject<GenericObject<Nullable<string>>[]>;
    const model = ref<LocalModel>(modelDehydrate(props.modelValue));
    const processedModels: Map<string, BundleTemplateProcessedModelItem[]> = reactive(new Map());
    const invalidLineUids = ref<Set<string>>(new Set());

    const listExpands = ref<string[]>(getExpandersList());
    const modalSaveBundleConfig = ref(false);

    const isInitialized = ref(false);
    const isDirty = ref(false);

    const unWatchLinesList = watch(
        listExpands,
        (newList, oldList) => {
            const changed = lo_xor(oldList, newList);
            const isAdded = newList.length > oldList.length;
            onListItemChange(changed, isAdded);
        }
    );

    function getExpandersList(): string[] {
        const lines = props.currentBundleTemplate?.lines;
        return lines ? Array.from(lines.values()).map((o) => o.uid) : [];
    }

    function onListItemChange(listUids: string[], isAdd: boolean): void {
        listUids.forEach((uid) => {
            if (!isAdd && model.value) {
                delete model.value[uid];
            }
        });

        setValue();
    }

    function onChange(
        lineUid: string,
        [models, processedModel]: [ GenericObject<string>[], BundleTemplateProcessedModelItem[] ]
    ): void {
        model.value[lineUid] = models;
        processedModels.set(lineUid, processedModel);
        setValue();

        if (isInitialized.value) {
            isDirty.value = true;
            emit('update:is-dirty', isDirty.value);
            emit('on-is-modified-change', true);
        }
    }

    function setValue(): void {
        const bundle = props.currentBundleTemplate;
        if (!bundle) return;

        const lines = props.currentBundleTemplate.lines;
        const output = getOutputModel(processedModels);
        invalidLineUids.value = validateProcessedModel(output, lines);
        const isValid = !invalidLineUids.value.size;

        emit('update:modelValue', output);
        emit('update:is-valid', isValid);
        debugConsole.info(`Bundle template output: (${isValid ? '' : 'in'}valid): `, output);
    }

    function onSaveBundleConfig(): void {
        modalSaveBundleConfig.value = true;
    }

    function onBundleSaved(
        bundle: Bundle
    ): void {
        emit('bundle-saved', bundle);
    }

    onMounted(async () => {
        await nextTick();
        isInitialized.value = true;
    });

    onBeforeUnmount(() => {
        unWatchLinesList();
    });
</script>

<style lang="scss"></style>
